import { useState } from 'react';
import { Button } from '../primitives';
import { StavbaPickerButton } from './StavbaPickerButton';
import { NewStavbaModal } from './NewStavbaModal';
import { useActiveStavba } from '../../features/stavba-context/StavbaContext';
import type { Stavba } from '../../api/schemas';
import styles from './StavbaGate.module.css';

/**
 * Prázdný stav pro moduly vázané na konkrétní stavbu (Harmonogram, RFI, Vady,
 * Fotodokumentace, Kontrolní dny, Stavební deník, Změnové listy…).
 * Místo slepé hlášky „vyberte stavbu v sidebaru“ nabídne přímo akci:
 * vybrat stavbu ze seznamu nebo rovnou založit novou — obojí nastaví aktivní
 * stavbu, takže uživatel může pokračovat bez odbočky na jinou stránku.
 */

interface Props {
  /** Krátký popis, proč modul potřebuje stavbu (zobrazí se pod nadpisem). */
  message?: string;
  /** Volitelná akce po výběru/založení stavby. */
  onReady?: (stavba: Stavba) => void;
}

export function StavbaGate({ message, onReady }: Props) {
  const { setActiveStavbaId, stavby } = useActiveStavba();
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className={styles.wrap}>
      <div className={styles.icon} aria-hidden>
        🏗
      </div>
      <h3 className={styles.title}>Vyberte nebo založte stavbu</h3>
      <p className={styles.text}>
        {message ?? 'Tento modul je vázán na konkrétní stavbu.'} Vyberte existující stavbu ze
        seznamu, nebo rovnou založte novou — agenda se pak naváže na ni.
      </p>

      <div className={styles.actions}>
        <StavbaPickerButton
          label="Vybrat stavbu"
          title="Vybrat stavbu ze seznamu"
          disabled={stavby.length === 0}
          onPick={(p) => {
            setActiveStavbaId(p.id);
            const picked = stavby.find((s) => s.id === p.id);
            if (picked) onReady?.(picked);
          }}
        />
        <Button variant="primary" onClick={() => setModalOpen(true)}>
          + Nová stavba
        </Button>
      </div>

      {stavby.length === 0 && (
        <p className={styles.hint}>Zatím nemáte žádnou stavbu — začněte tlačítkem „+ Nová stavba“.</p>
      )}

      <NewStavbaModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={(s) => onReady?.(s)}
      />
    </div>
  );
}
